import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

import { useHelp } from "@/components/layout/HelpProvider";
import {
  pageContentPaddingClassName,
  pageContentPaddingHelpDockedClassName,
} from "@/components/layout/pageLayout";
import { useIsMobile } from "@/hooks/useIsMobile";
import { PageScrollContext } from "@/components/layout/usePageScrollRoot";
import { touchActivity } from "@/lib/sessionActivity";
import { cn } from "@/lib/utils";

export function PageScrollProvider({ children }: { children: ReactNode }): ReactElement {
  const [scrollRoot, setScrollRootState] = useState<HTMLElement | null>(null);
  const setScrollRoot = useCallback((node: HTMLElement | null) => {
    setScrollRootState(node);
  }, []);
  const value = useMemo(() => ({ scrollRoot, setScrollRoot }), [scrollRoot, setScrollRoot]);

  useEffect(() => {
    if (!scrollRoot) return;
    function onScroll(): void {
      touchActivity();
    }
    scrollRoot.addEventListener("scroll", onScroll, { passive: true });
    return () => scrollRoot.removeEventListener("scroll", onScroll);
  }, [scrollRoot]);

  return <PageScrollContext.Provider value={value}>{children}</PageScrollContext.Provider>;
}

type PageScrollViewportProps = {
  children: ReactNode;
};

/** Full-width scroll area for the main column; content inset lives on the inner wrapper. */
export function PageScrollViewport({ children }: PageScrollViewportProps): ReactElement {
  const ctx = useContext(PageScrollContext);
  const { pathname } = useLocation();
  const { chatOpen } = useHelp();
  const isMobile = useIsMobile();
  const helpDocked = chatOpen && !isMobile;
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const registerScrollRoot = ctx?.setScrollRoot;

  const setScrollRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      viewportRef.current = node;
      registerScrollRoot?.(node);
    },
    [registerScrollRoot],
  );

  useLayoutEffect(() => {
    viewportRef.current?.scrollTo({ top: 0, left: 0 });
  }, [pathname]);

  if (!ctx) {
    throw new Error("PageScrollViewport must be used within PageScrollProvider");
  }

  return (
    <div
      ref={setScrollRootRef}
      className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-y-contain"
    >
      <div
        className={cn(
          "group/page-pad flex min-h-full min-w-0 flex-col",
          pageContentPaddingClassName,
          helpDocked && pageContentPaddingHelpDockedClassName,
        )}
        data-help-dock={helpDocked ? "open" : undefined}
      >
        {children}
      </div>
    </div>
  );
}
